import React, { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import styled, { useTheme } from 'styled-components/native'

import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { InboxIcon } from '../../atoms/icons/InboxIcon'
import { LeaveSiteIcon } from '../../atoms/icons/LeaveSiteIcon'
import { SideBarIcon } from '../../atoms/icons/SideBarIcon'
import { UnreadMessageIllustration } from '../../atoms/illustrations/UnreadMessageIllustration'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Title } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { HorizontalTabs, TextCasing } from '../../molecules/horizontalTabs/HorizontalTabs'
import { ProviderComposeTypeahead } from '../../molecules/providerComposeTypeahead/ProviderComposeTypeahead'
import { TitleSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export type ChatSideBarProps = React.PropsWithChildren<{
  clientName?: string
  title?: React.ReactNode
  titleAccessory?: React.ReactNode
  onOpenDetailsPanel: () => void
  isDetailsPanelOpen: boolean
  isComposing: boolean
  clients: { id: string; name: string }[]
  onClientSelected: (clientId: string | null) => void
  onOpenNewWindow?: () => void
  onClose?: () => void
  onOpenMessages?: () => void
  tabs: { id: string; label: React.ReactNode; badgeCount?: number; iconWithTooltip?: React.ReactNode }[]
  selectedTabId: string
  onTabSelected: (tabId: string) => void
  handleClientHeadingPressed?: () => void
  markMessagesUnread?: () => void
  patientFullName?: string
  guardianName?: string
  headerNote?: string
  isTitleMuted?: boolean
}>

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  backgroundColor: theme.colors.backgroundPrimary,
  zIndex: 'unset',
}))

const HeaderColumn = styled.View(({ theme }) => ({
  flexDirection: 'column',
  borderBottomColor: theme.colors.borderDefault,
  borderBottomWidth: '1px',
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
}))

const HeaderRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  height: '75px',
  justifyContent: 'space-between',
})

const HeaderIcons = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const IconContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['12px'],
}))

const MarkMessagesUnreadContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['4px'],
  marginTop: theme.spacing['4px'],
}))

const ChatTitleContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const HeaderNoteContainer = styled.View({
  flexBasis: '100%',
})

const TooltipButtonContainer = styled.View({
  position: 'relative',
})

const PatientNameContainer = styled.View({
  position: 'relative',
})

const SidebarIconContainer = styled.View({
  position: 'relative',
})

export const ProviderChatHeader: React.FC<ChatSideBarProps> = ({
  clientName,
  title,
  titleAccessory,
  onOpenDetailsPanel,
  isDetailsPanelOpen,
  isComposing,
  clients,
  onClientSelected,
  onOpenNewWindow,
  onClose,
  onOpenMessages,
  tabs,
  selectedTabId,
  onTabSelected,
  handleClientHeadingPressed,
  markMessagesUnread,
  patientFullName,
  guardianName,
  headerNote,
  isTitleMuted = false,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const sideBarIconOpacity = useSharedValue(0)
  const newWindowIconRight = useSharedValue(0)
  const patientNameTooltipRef = useRef<{ show: () => void; hide: () => void }>(null)
  const openWindowTooltipRef = useRef<{ show: () => void; hide: () => void }>(null)
  const markMessagesUnreadTooltipRef = useRef<{ show: () => void; hide: () => void }>(null)
  const sideBarTooltipRef = useRef<{ show: () => void; hide: () => void }>(null)
  const hasTitleMetadata = Boolean(headerNote || titleAccessory)
  const hasGuardian = Boolean(patientFullName && guardianName)
  const heading = isComposing ? (
    <FormattedMessage defaultMessage='New message' description='Header for page when composing a new message' />
  ) : title ? (
    title
  ) : hasGuardian ? (
    <FormattedMessage
      defaultMessage="{guardianName} ({patientFullName}'s caregiver)"
      description='Chat header showing guardian name with patient relationship'
      values={{ guardianName, patientFullName }}
    />
  ) : (
    clientName
  )
  useEffect(() => {
    const hideSideBarIcon = isDetailsPanelOpen || (isComposing && !clientName)
    sideBarIconOpacity.value = hideSideBarIcon ? withTiming(0) : withTiming(1)
  }, [clientName, isComposing, isDetailsPanelOpen, newWindowIconRight, sideBarIconOpacity])

  const sideBarButtonStyle = useAnimatedStyle(() => ({
    opacity: sideBarIconOpacity.value,
  }))

  return (
    <Container testID={tID('ProviderChatHeader')}>
      <HeaderColumn>
        <HeaderRow
          style={hasTitleMetadata ? { height: 'auto' as const, minHeight: 75, paddingVertical: 8 } : undefined}
        >
          {(!!clientName || !!title || isComposing) && (
            <>
              <ChatTitleContainer style={hasTitleMetadata ? { flex: 1, flexWrap: 'wrap', minWidth: 0 } : undefined}>
                {handleClientHeadingPressed ? (
                  <PatientNameContainer>
                    <Tooltip
                      ref={patientNameTooltipRef}
                      content={
                        <FormattedMessage
                          defaultMessage='Show client details'
                          description='Tooltip for client name button to show client details'
                        />
                      }
                      hoverEnabled
                      style={{
                        position: 'absolute',
                        top: 20,
                        left: '50%',
                        marginTop: 8,
                      }}
                    >
                      <></>
                    </Tooltip>
                    <PressableOpacity
                      onPress={handleClientHeadingPressed}
                      onHoverIn={() => patientNameTooltipRef.current?.show()}
                      onHoverOut={() => patientNameTooltipRef.current?.hide()}
                      accessibilityLabel={formatMessage({
                        defaultMessage: 'Show client details',
                        description: 'Accessibility label for button to show client details',
                      })}
                    >
                      <Title
                        size={TitleSize.MEDIUM}
                        text={heading}
                        color={isTitleMuted ? theme.colors.textSecondary : undefined}
                        style={{ textDecorationLine: 'underline' }}
                      />
                    </PressableOpacity>
                  </PatientNameContainer>
                ) : (
                  <Title
                    size={TitleSize.MEDIUM}
                    text={heading}
                    wrap={hasTitleMetadata}
                    color={isTitleMuted ? theme.colors.textSecondary : undefined}
                  />
                )}
                {titleAccessory}
                {onOpenNewWindow && (
                  <TooltipButtonContainer>
                    <Tooltip
                      ref={openWindowTooltipRef}
                      content={
                        <FormattedMessage
                          defaultMessage='Open in new window'
                          description='Tooltip for open in new window button'
                        />
                      }
                      style={{
                        position: 'absolute',
                        top: 30,
                        left: '50%',
                        marginTop: 8,
                      }}
                      hoverEnabled
                    >
                      <></>
                    </Tooltip>
                    <TertiaryIconButton
                      testID={tID('ProviderChatHeader-new-window-button')}
                      size={ButtonSize.UNSIZED}
                      leftIcon={
                        <IconContainer>
                          <LeaveSiteIcon size={20} fillColor={theme.colors.iconDefault} />
                        </IconContainer>
                      }
                      onPress={onOpenNewWindow}
                      onHoverIn={() => openWindowTooltipRef.current?.show()}
                      onHoverOut={() => openWindowTooltipRef.current?.hide()}
                      accessibilityLabel={formatMessage({
                        defaultMessage: 'Open in new window',
                        description: 'Accessibility label for open in new window button',
                      })}
                    />
                  </TooltipButtonContainer>
                )}
                {markMessagesUnread && (
                  <TooltipButtonContainer>
                    <Tooltip
                      ref={markMessagesUnreadTooltipRef}
                      content={
                        <FormattedMessage
                          defaultMessage='Mark message as unread'
                          description='Tooltip for mark message as unread button'
                        />
                      }
                      style={{
                        position: 'absolute',
                        top: 30,
                        left: '50%',
                        marginTop: 8,
                      }}
                      hoverEnabled
                    >
                      <></>
                    </Tooltip>
                    <TertiaryIconButton
                      testID={tID('ProviderChatHeader-mark-unread-button')}
                      size={ButtonSize.UNSIZED}
                      leftIcon={
                        <MarkMessagesUnreadContainer>
                          <UnreadMessageIllustration size={25} fillColor={theme.colors.iconDefault} />
                        </MarkMessagesUnreadContainer>
                      }
                      onPress={() => markMessagesUnread()}
                      onHoverIn={() => markMessagesUnreadTooltipRef.current?.show()}
                      onHoverOut={() => markMessagesUnreadTooltipRef.current?.hide()}
                      accessibilityLabel={formatMessage({
                        defaultMessage: 'Mark message as unread',
                        description: 'Accessibility label for mark message as unread button',
                      })}
                    />
                  </TooltipButtonContainer>
                )}
                {Boolean(headerNote) && (
                  <HeaderNoteContainer>
                    <BodyText
                      testID={tID('ProviderChatHeader-note')}
                      size={BodyTextSize.SMALL}
                      color={theme.colors.textSecondary}
                      text={headerNote}
                    />
                  </HeaderNoteContainer>
                )}
              </ChatTitleContainer>
              <HeaderIcons style={hasTitleMetadata ? { flexShrink: 0 } : undefined}>
                <Animated.View style={sideBarButtonStyle}>
                  <SidebarIconContainer>
                    <Tooltip
                      ref={sideBarTooltipRef}
                      content={
                        <FormattedMessage
                          defaultMessage='Open sidebar'
                          description='Tooltip for open the sidebar button'
                        />
                      }
                      style={{
                        position: 'absolute',
                        top: 30,
                        left: '50%',
                        marginTop: 8,
                      }}
                      hoverEnabled
                    >
                      <></>
                    </Tooltip>
                    <TertiaryIconButton
                      testID={tID('ProviderChatHeader-side-bar-button')}
                      pointerEvents={isDetailsPanelOpen ? 'none' : undefined}
                      size={ButtonSize.UNSIZED}
                      leftIcon={
                        <IconContainer>
                          <SideBarIcon />
                        </IconContainer>
                      }
                      onPress={onOpenDetailsPanel}
                      onHoverIn={() => sideBarTooltipRef.current?.show()}
                      onHoverOut={() => sideBarTooltipRef.current?.hide()}
                      accessibilityLabel={formatMessage({
                        defaultMessage: 'Open sidebar',
                        description: 'Accessibility label for open the sidebar button',
                      })}
                    />
                  </SidebarIconContainer>
                </Animated.View>
                {onOpenMessages && (
                  <Tooltip
                    asChild
                    content={formatMessage({
                      defaultMessage: 'Open messaging page',
                      description: 'Tooltip for opening Messages Center from a client profile',
                    })}
                    hoverEnabled
                    placement='bottom'
                  >
                    <TertiaryIconButton
                      testID={tID('ProviderChatHeader-inbox-button')}
                      size={ButtonSize.UNSIZED}
                      leftIcon={
                        <IconContainer>
                          <InboxIcon size={18} fillColor={theme.colors.iconDefault} />
                        </IconContainer>
                      }
                      onPress={onOpenMessages}
                      accessibilityLabel={formatMessage({
                        defaultMessage: 'Open messaging page',
                        description: 'Accessibility label for opening Messages Center from a client profile',
                      })}
                    />
                  </Tooltip>
                )}
                {onClose && (
                  <TertiaryIconButton
                    testID={tID('ProviderChatHeader-close-button')}
                    size={ButtonSize.UNSIZED}
                    leftIcon={
                      <IconContainer>
                        <CloseIcon size={16} fillColor={theme.colors.iconDefault} />
                      </IconContainer>
                    }
                    onPress={onClose}
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Close messages',
                      description: 'Accessibility label for closing client profile messaging',
                    })}
                  />
                )}
              </HeaderIcons>
            </>
          )}
        </HeaderRow>
        {tabs.length > 1 && (
          <HorizontalTabs
            testID={tID('ProviderChatHeader')}
            tabs={tabs}
            onPress={onTabSelected}
            selectedTabId={selectedTabId}
            badgeCountColor={theme.colors.iconActive}
            scrollable
            textCasing={TextCasing.AS_PROVIDED}
          />
        )}
      </HeaderColumn>
      {isComposing && <ProviderComposeTypeahead clients={clients} onClientSelected={onClientSelected} />}
    </Container>
  )
}
